import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import {
  Headset, Gauge, UsersThree, SignOut, LockKey, SquaresFour, FileText, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { CxEntityProvider, useCxEntity, EntityScope } from './cxShared';
import CxOverview from './CxOverview';
import CxCustomers from './CxCustomers';
import CxReports from './CxReports';
import CxCampaigns from './CxCampaigns';

const TABS = [
  { v: 'overview',  l: 'Overview',  Ic: Gauge,      C: CxOverview },
  { v: 'customers', l: 'Customers', Ic: UsersThree, C: CxCustomers },
  { v: 'campaigns', l: 'Campaigns', Ic: PaperPlaneTilt, C: CxCampaigns },
  { v: 'reports',   l: 'Reports',   Ic: FileText,   C: CxReports },
];

function WorkspaceInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { entityId, setEntityId } = useCxEntity();
  const [tab, setTab] = useState('overview');
  const canExit = user?.role === 'super_admin' || user?.role === 'entity_admin';

  return (
    <div className="min-h-screen bg-[#FCFAF6]" data-testid="cx-workspace-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A2540] text-white border-b border-[#0A2540]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-[#C9A35B]/20 text-[#C9A35B] flex items-center justify-center shrink-0">
              <Headset size={18} weight="duotone" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Customer Experience
              </p>
              <p className="text-sm font-semibold truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {user?.entity_name || user?.fullname || 'Workspace'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canExit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                data-testid="cx-exit-workspace"
                className="text-white/80 hover:text-white hover:bg-white/10 h-9"
              >
                <SquaresFour size={16} className="mr-1.5" /> Full dashboard
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center font-semibold text-xs"
                  data-testid="cx-avatar"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {(user?.fullname || user?.username || 'U').slice(0, 1).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <DropdownMenuLabel>
                  <p className="text-xs font-semibold text-[#1A1D1C] truncate">{user?.fullname || user?.username}</p>
                  <p className="text-[10px] text-[#78716C] truncate">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/change-password')}
                  data-testid="cx-change-password"
                  className="cursor-pointer text-[#0A2540] focus:bg-[#0A2540]/5"
                >
                  <LockKey size={14} className="mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="cx-logout"
                  className="text-[#EF4444] focus:text-[#EF4444] focus:bg-[#EF4444]/10 cursor-pointer"
                >
                  <SignOut size={14} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Customer Experience
            </h1>
          </div>
          <EntityScope value={entityId} onChange={setEntityId} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-white border border-[#EDE6D8] rounded-xl p-1 h-auto flex flex-wrap">
            {TABS.map(({ v, l, Ic }) => (
              <TabsTrigger
                key={v}
                value={v}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
                data-testid={`cx-tab-${v}`}
              >
                <Ic size={14} weight="duotone" /> {l}
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map(({ v, C }) => (
            <TabsContent key={v} value={v} className="mt-0"><C /></TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}

export default function CxWorkspace() {
  return (
    <CxEntityProvider>
      <WorkspaceInner />
    </CxEntityProvider>
  );
}
